import React, { useContext, useState, FormEvent, useEffect } from "react";
import { JoinTheClubData } from "../mentorsSurvey.model";
import { TranslationContext } from "@src/components/translation/TranslationContext";
import { Button, Space, Subheading, BodyText, TextField, TextArea, Caption } from "@src/components/nessie-web";
import { StyledForm, CheckboxContainer } from "../joinTheClub.style";
import { logEvent } from "@src/utils/logClient";
import { useStaticQuery, graphql } from "gatsby";
import { AppDataContext } from "@src/components/AppDataContext";
import { isNil, omitBy } from "lodash";

const maxAllowedBirthDate = new Date(new Date().setFullYear(new Date().getFullYear() - 13));
const maxAllowedBirthDateString = `${maxAllowedBirthDate.getFullYear()}-${maxAllowedBirthDate
  .getUTCMonth()
  .toString()
  .padStart(2, "0")}-${maxAllowedBirthDate.getUTCDay().toString().padStart(2, "0")}`;

const JoinTheClubModal = (props: { closeModal: () => void }) => {
  const {
    directus: {
      page_mentors_surveys: { new_form_1_headline_link_url },
    },
  } = useStaticQuery(graphql`
    query {
      directus {
        page_mentors_surveys {
          new_form_1_headline_link_url
        }
      }
    }
  `);

  const t = useContext(TranslationContext);
  const { data: appData, services } = useContext(AppDataContext);
  const [userDataLoaded, setUserDataLoaded] = useState(false);
  const [jtcFormData, setJtcFormData] = useState<JoinTheClubData>({
    question1: "",
    question2: "",
    question3: null,
    question4: "",
  });

  function handleSubmit(ev: FormEvent) {
    ev.preventDefault();
    const logData = {
      question1: jtcFormData.question1,
      question2: jtcFormData.question2,
      question3: jtcFormData.question3,
      question4: jtcFormData.question4,
    };

    const application = omitBy(
      {
        birthday: jtcFormData.question3,
        intentions: jtcFormData.question4,
      },
      isNil,
    );

    services.applyToBeMentor(application);

    logEvent({
      eventValue: window.location.href,
      eventName: "web.external_page.mentors_page.join_the_club_form.submit",
      metadata: logData,
    });
    setTimeout(props.closeModal, 3000);
  }

  useEffect(() => {
    logEvent("web.external.mentors_page.join_the_club_modal.modal_open");
  }, []);

  useEffect(() => {
    if (appData.type && appData.userData) {
      const { firstName, lastName, emailAddress } = appData.userData;
      setJtcFormData({
        ...jtcFormData,
        question1: `${firstName} ${lastName}`,
        question2: emailAddress ?? "",
      });
      setUserDataLoaded(true);
    }
  }, [appData]);

  return (
    <>
      <BodyText>
        {t.translate("directus.page_mentors_surveys.new_form_1_headline_1")}{" "}
        <a href={new_form_1_headline_link_url} target="_blank" rel="noreferrer">
          {t.translate("directus.page_mentors_surveys.new_form_1_headline_link_text")}
        </a>
      </BodyText>
      <Space size="m" />
      <StyledForm onSubmit={(e) => handleSubmit(e)}>
        <Space size="m" />
        <div>
          <Subheading>{t.translate("directus.page_mentors_surveys.new_form_1_questions.question_1")}</Subheading>
          <Space size="xs" />
          <TextField
            type="text"
            required
            disabled={userDataLoaded}
            name="question1"
            value={jtcFormData.question1}
            onChange={(value: string) => setJtcFormData({ ...jtcFormData, question1: value })}
          />
        </div>
        <Space size="m" />
        <div>
          <Subheading>{t.translate("directus.page_mentors_surveys.new_form_1_questions.question_2")}</Subheading>
          <Space size="xs" />
          <TextField
            type="email"
            required
            disabled={userDataLoaded}
            name="question2"
            value={jtcFormData.question2}
            onChange={(value: string) => setJtcFormData({ ...jtcFormData, question2: value })}
          />
        </div>
        <Space size="m" />
        <div>
          <Subheading>{t.translate("directus.page_mentors_surveys.new_form_1_questions.question_3")}</Subheading>
          <Space size="xs" />
          <TextField
            type="date"
            required
            name="question3"
            onChange={(value: Date) => setJtcFormData({ ...jtcFormData, question3: value })}
            max={maxAllowedBirthDateString}
          />
        </div>
        <Space size="m" />
        <div>
          <BodyText>{t.translate("directus.page_mentors_surveys.new_form_1_questions.question_4")}</BodyText>
          <Space size="xs" />
          <TextArea
            name="question4"
            required
            onChange={(value: string) => setJtcFormData({ ...jtcFormData, question4: value })}
          />
          <Caption kind="inactive">Minimum 50 characters</Caption>
        </div>
        <Space size="m" />
        <Button kind="primary" type="submit" size="m">
          {t.translate("directus.page_mentors_surveys.new_form_1_submit_label")}
        </Button>
      </StyledForm>
    </>
  );
};

export default JoinTheClubModal;
